/*
 * Copyright 2026 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

function getTopmostSelectedIndex(elements, selectedIds) {
  for (let index = elements.length - 1; index >= 0; index--) {
    if (selectedIds.has(elements[index].id)) {
      return index;
    }
  }
  return -1;
}

function getGroupInfo(elements, groupId) {
  const groupElementIds = [];
  let groupEndIndex = -1;

  for (let index = 0; index < elements.length; index++) {
    const element = elements[index];
    if (element.groupId === groupId) {
      groupElementIds.push(element.id);
      groupEndIndex = index;
    }
  }

  return { groupElementIds, groupEndIndex };
}

function getPastePlacement({
  elements = [],
  selectedElementIds = [],
  pastedGroups = {},
}) {
  const isPastingGroup = Boolean(Object.keys(pastedGroups).length);
  const selection = Array.isArray(selectedElementIds)
    ? selectedElementIds
    : [];
  const selectedIds = new Set(selection);
  const anchorIndex = getTopmostSelectedIndex(elements, selectedIds);
  const anchorElement = anchorIndex === -1 ? null : elements[anchorIndex];

  if (!anchorElement) {
    return {
      insertAt: elements.length,
      targetGroupId: null,
      isPastingGroup,
    };
  }

  const { groupId } = anchorElement;

  if (groupId) {
    const { groupElementIds, groupEndIndex } = getGroupInfo(elements, groupId);
    const isGroupSelection =
      groupElementIds.length > 0 &&
      selection.length === groupElementIds.length &&
      groupElementIds.every((id) => selectedIds.has(id));

    if (isPastingGroup) {
      return {
        insertAt: groupEndIndex + 1,
        targetGroupId: null,
        isPastingGroup,
      };
    }

    if (isGroupSelection) {
      return {
        insertAt: groupEndIndex + 1,
        targetGroupId: groupId,
        isPastingGroup,
      };
    }

    return {
      insertAt: anchorIndex + 1,
      targetGroupId: groupId,
      isPastingGroup,
    };
  }

  return {
    insertAt: anchorIndex + 1,
    targetGroupId: null,
    isPastingGroup,
  };
}

export default getPastePlacement;
export { getPastePlacement };
